import { useState, type ReactNode } from "react";
import {
  AlertTriangle,
  Briefcase,
  Building2,
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  FileSpreadsheet,
  HelpCircle,
  MapPin,
  MinusCircle,
  PenLine,
  Users,
  XCircle,
} from "lucide-react";
import type {
  CfoEvidence,
  CfoExplainedCheck,
  CfoExplainedInput,
  CfoExplainedRule,
  CfoScoreExplanation,
} from "@/api/types";
import { CFO_DECISIONS as DECISIONS, withCountry } from "@/lib/cfoLabels";
import { formatDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";

const STATUS: Partial<Record<CfoExplainedRule["status"], { label: string; className: string }>> = {
  MISSING_DATA: { label: "Missing data", className: "bg-amber-50 text-amber-700" },
  PROVISIONAL: { label: "Provisional", className: "bg-sky-50 text-sky-700" },
  DISABLED: { label: "Not scored", className: "bg-slate-100 text-slate-500" },
  UNAVAILABLE: { label: "Unavailable", className: "bg-slate-100 text-slate-500" },
};

const INPUT_STATES: Record<CfoExplainedInput["state"], { label: string; className: string }> = {
  verified: { label: "Verified", className: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  none_found: { label: "None found", className: "bg-slate-100 text-slate-600 ring-slate-200" },
  manual: { label: "Answered", className: "bg-violet-50 text-violet-700 ring-violet-200" },
  missing: { label: "Missing", className: "bg-amber-50 text-amber-800 ring-amber-200" },
};

/** The M&A bar palette: empty, low, mid, high. A rule still waiting for
 *  data is striped, so a provisional contribution never reads as final. */
function barStyle(rule: CfoExplainedRule, pct: number): { className: string; striped: boolean } {
  if (rule.status === "MISSING_DATA") return { className: "", striped: true };
  if (!rule.points) return { className: "bg-slate-200", striped: false };
  if (pct >= 0.75) return { className: "bg-emerald-500", striped: false };
  if (pct >= 0.4) return { className: "bg-indigo-500", striped: false };
  return { className: "bg-amber-500", striped: false };
}

const STRIPES =
  "repeating-linear-gradient(45deg, rgb(252 211 77) 0 4px, rgb(254 243 199) 4px 8px)";

function SourceIcon({ source }: { source: string }) {
  const s = source.toLowerCase();
  const Icon = s.includes("manual")
    ? PenLine
    : s.includes("crunchbase") || s.includes("export")
      ? FileSpreadsheet
      : s.includes("job")
        ? Briefcase
        : s.includes("team")
          ? Users
          : s.includes("profile")
            ? MapPin
            : Building2;
  return (
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-white text-slate-500 ring-1 ring-slate-200">
      <Icon className="h-3 w-3" />
    </span>
  );
}

export function EvidenceItem({ evidence }: { evidence: CfoEvidence }) {
  const counted = evidence.counted !== false;
  const meta = [
    evidence.source,
    evidence.detail,
    evidence.observed_at ? formatDate(evidence.observed_at) : null,
  ].filter(Boolean);
  return (
    <li className={cn("flex items-start gap-2", !counted && "opacity-60")}>
      <SourceIcon source={evidence.source} />
      <div className="min-w-0 flex-1">
        <p className="break-words text-xs text-slate-800">
          {withCountry(evidence.text)}
          {!counted && (
            <span className="ml-1.5 rounded bg-slate-100 px-1 py-px text-[10px] font-medium uppercase tracking-wide text-slate-500">
              Not counted
            </span>
          )}
        </p>
        <p className="break-words text-[11px] text-slate-500">{meta.join(" · ")}</p>
      </div>
      {evidence.url && (
        <a
          href={evidence.url}
          target="_blank"
          rel="noreferrer noopener"
          title="Open the source"
          className="mt-0.5 shrink-0 text-slate-400 hover:text-indigo-600"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
    </li>
  );
}

export function EvidenceList({ evidence }: { evidence: CfoEvidence[] }) {
  if (!evidence.length) return null;
  return (
    <ul className="mt-2 space-y-1.5 border-t border-slate-100 pt-2">
      {evidence.map((item, index) => (
        <EvidenceItem key={`${item.source}-${item.text}-${index}`} evidence={item} />
      ))}
    </ul>
  );
}

export function InputBlock({ input }: { input: CfoExplainedInput }) {
  const state = INPUT_STATES[input.state];
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/70 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
            {input.label}
          </p>
          <p className="mt-0.5 break-words text-sm text-slate-900">{input.value ?? "—"}</p>
        </div>
        <span
          title={input.state_label}
          className={cn(
            "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
            state.className,
          )}
        >
          {state.label}
        </span>
      </div>
      <p className="mt-1 text-[11px] text-slate-500">
        {[input.state_label, input.coverage].filter(Boolean).join(" · ")}
      </p>
      <EvidenceList evidence={input.evidence ?? []} />
    </div>
  );
}

function RuleRow({ rule }: { rule: CfoExplainedRule }) {
  const [open, setOpen] = useState(false);
  const scored = rule.status !== "DISABLED" && rule.status !== "UNAVAILABLE";
  const points = rule.points ?? 0;
  const pct = scored && rule.max_points ? Math.max(0, Math.min(1, points / rule.max_points)) : 0;
  const bar = barStyle(rule, pct);
  const status = STATUS[rule.status];
  return (
    <div className="rounded-lg">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="group w-full rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-slate-50"
      >
        <div className="flex items-baseline justify-between gap-3 text-sm">
          <span className="flex min-w-0 items-center gap-1.5">
            <ChevronRight
              className={cn(
                "h-3.5 w-3.5 shrink-0 self-center text-slate-400 transition-transform",
                open && "rotate-90",
              )}
            />
            <span className={cn("truncate", scored ? "text-slate-700" : "text-slate-400")}>
              {rule.label}
            </span>
            {status && (
              <span
                className={cn(
                  "shrink-0 rounded px-1.5 py-px text-[10px] font-medium uppercase tracking-wide",
                  status.className,
                )}
              >
                {status.label}
              </span>
            )}
          </span>
          <span className="flex shrink-0 items-baseline gap-3">
            {rule.tier && (
              <span
                title={rule.tier}
                className="hidden max-w-[220px] truncate text-xs text-slate-500 sm:inline"
              >
                {rule.tier}
              </span>
            )}
            {scored ? (
              <span
                className={cn(
                  "font-medium tabular-nums",
                  points === 0
                    ? "text-slate-400"
                    : pct >= 0.75
                      ? "text-emerald-600"
                      : pct >= 0.4
                        ? "text-indigo-600"
                        : "text-amber-600",
                )}
              >
                {points}
                <span className="text-slate-400">/{rule.max_points}</span>
              </span>
            ) : (
              <span className="text-xs text-slate-400">—</span>
            )}
          </span>
        </div>
        {scored && (
          <div className="ml-5 mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div
              className={cn("h-full rounded-full transition-all duration-500", bar.className)}
              style={{
                width: `${bar.striped ? Math.max(pct, 0.08) * 100 : pct * 100}%`,
                backgroundImage: bar.striped ? STRIPES : undefined,
              }}
            />
          </div>
        )}
      </button>
      {open && (
        <div className="ml-5 mt-2 space-y-2 pb-2">
          {rule.tier && (
            <p className="text-xs text-slate-600">
              <span className="text-slate-400">Tier: </span>
              {rule.tier}
            </p>
          )}
          {(rule.inputs ?? []).map((input) => (
            <InputBlock key={`${rule.key}-${input.dimension}`} input={input} />
          ))}
          {(rule.notes ?? []).map((note) => (
            <p key={note} className="text-[11px] italic text-slate-500">
              {note}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

const CHECKS: Record<
  CfoExplainedCheck["outcome"],
  { icon: typeof CheckCircle2; className: string; label: string }
> = {
  clear: { icon: CheckCircle2, className: "text-emerald-600", label: "Clear" },
  excluded: { icon: XCircle, className: "text-rose-600", label: "Excluded" },
  unknown: { icon: HelpCircle, className: "text-amber-600", label: "Not known yet" },
  disabled: { icon: MinusCircle, className: "text-slate-400", label: "Switched off" },
};

function CheckItem({ check }: { check: CfoExplainedCheck }) {
  const [open, setOpen] = useState(false);
  const style = CHECKS[check.outcome];
  const Icon = style.icon;
  const evidence = check.evidence ?? [];
  return (
    <li className="rounded-lg border border-slate-100 bg-white">
      <button
        type="button"
        aria-expanded={open}
        disabled={!evidence.length}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-2.5 py-2 text-left disabled:cursor-default"
      >
        <Icon className={cn("h-4 w-4 shrink-0", style.className)} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-medium text-slate-700">{check.label}</span>
          <span className="block truncate text-[11px] text-slate-500">
            {[style.label, check.value].filter(Boolean).join(" · ")}
          </span>
        </span>
        {evidence.length > 0 && (
          <ChevronRight
            className={cn("h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform", open && "rotate-90")}
          />
        )}
      </button>
      {open && (
        <div className="px-2.5 pb-2">
          <EvidenceList evidence={evidence} />
        </div>
      )}
    </li>
  );
}

/**
 * The CFO score in the M&A "Signal intelligence" layout: the total, then
 * one bar per rule of the grid. Each rule opens on the data it read and
 * the justification of each value (the funding row, the job postings, the
 * team members, the founders' places, a reviewer's signed answer).
 */
export function CfoScoreCard({
  explanation,
  notice,
  actions,
}: {
  explanation: CfoScoreExplanation;
  /** Shown above the rules, e.g. "not current". */
  notice?: ReactNode;
  /** Trailing header controls (re-evaluate, refresh). */
  actions?: ReactNode;
}) {
  const decision = DECISIONS[explanation.decision] ?? {
    label: explanation.decision,
    className: "bg-slate-100 text-slate-700 ring-slate-200",
  };
  const score = explanation.raw_score;
  const max = explanation.max_score || 100;
  const threshold = explanation.threshold ?? null;
  const ranged =
    explanation.lower != null && explanation.upper != null && explanation.lower !== explanation.upper;
  const rules = explanation.rules ?? [];
  const checks = explanation.checks ?? [];
  const scored = rules.filter((r) => r.status !== "DISABLED");
  const disabled = rules.filter((r) => r.status === "DISABLED");
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          <h3 className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            Signal intelligence
          </h3>
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
                decision.className,
              )}
            >
              {decision.label}
            </span>
            {explanation.provisional && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                Provisional
              </span>
            )}
            {ranged && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                Range {explanation.lower}–{explanation.upper}
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-start gap-2">
          {actions}
          <span className="text-xs font-semibold text-indigo-600">
            {score ?? "—"}
            <span className="text-slate-400">/{max}</span>
            <span className="ml-1 text-[10px] uppercase tracking-wider text-slate-400">total</span>
          </span>
        </div>
      </div>

      <div>
        <div className="relative h-2 overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full bg-indigo-500 transition-all duration-500"
            style={{ width: `${Math.max(0, Math.min(1, (score ?? 0) / max)) * 100}%` }}
          />
          {threshold != null && (
            <span
              className="absolute top-0 h-full w-0.5 bg-slate-900/60"
              style={{ left: `${Math.min(1, threshold / max) * 100}%` }}
            />
          )}
        </div>
        <p className="mt-1 flex justify-between text-[11px] text-slate-500">
          <span>
            {threshold != null ? `Qualified from ${threshold}` : "No threshold"}
          </span>
          <span>Evaluated {formatDate(explanation.evaluated_at)}</span>
        </p>
      </div>

      {notice}

      {(explanation.exclusions ?? []).length > 0 && (
        <div className="flex gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div>
            <p className="font-medium">Excluded</p>
            <p className="mt-0.5">{(explanation.exclusions ?? []).join(" · ")}</p>
          </div>
        </div>
      )}
      {(explanation.reasons ?? []).length > 0 && (
        <ul className="space-y-0.5 text-xs text-slate-500">
          {(explanation.reasons ?? []).map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      )}

      <div className="space-y-1">
        {scored.map((rule) => (
          <RuleRow key={rule.key} rule={rule} />
        ))}
        {disabled.map((rule) => (
          <RuleRow key={rule.key} rule={rule} />
        ))}
      </div>

      {checks.length > 0 && (
        <div>
          <h4 className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            Exclusion checks
          </h4>
          <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
            {checks.map((check) => (
              <CheckItem key={`${check.label}-${check.field}`} check={check} />
            ))}
          </ul>
        </div>
      )}

      {!explanation.personal_details && (
        <p className="text-[11px] text-slate-400">
          Names of people are hidden: they need the right to view all companies.
        </p>
      )}
    </div>
  );
}
